import React, { useEffect, useMemo, useState } from "react";
import { Mail, Phone, Search, X } from "lucide-react";
import "./AddNewProject.css";
import "../../../styles/portalTypography.css";
import apiClient, { getFriendlyErrorMessage } from "../../../app/apiClient";
import { formatRupees } from "../../../utils/formatRupees";

function AddNewProject({ onClose = () => {}, onCreated = () => {} }) {
  const [formData, setFormData] = useState({
    projectName: "",
    description: "",
    location: "",
    address: "",
    area: "",
    foundation: "",
    category: "",
    client: "",
    cost: "",
    startDate: "",
    expectedEndDate: "",
  });
  const [clientQuery, setClientQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [clientLoadError, setClientLoadError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadClients = async () => {
      try {
        const { data: responseData } = await apiClient.get("/admin/client");
        const responseClients = Array.isArray(responseData)
          ? responseData
          : responseData?.clients || responseData?.data || [];

        if (!Array.isArray(responseClients)) {
          throw new Error("The server returned an invalid client list.");
        }

        if (isMounted) {
          setClients(
            responseClients.map((client) => ({
              id: client.id || client._id || client.clientId || null,
              name: client.userName || client.fullName || "Unnamed client",
              email: client.email || "",
              phone: client.phoneNumber || "",
            })),
          );
        }
      } catch (error) {
        console.error("Load project clients request failed:", error);
        if (isMounted) {
          setClientLoadError(
            getFriendlyErrorMessage(
              error,
              "We could not load the client list. Please try again.",
            ),
          );
        }
      } finally {
        if (isMounted) setIsLoadingClients(false);
      }
    };

    loadClients();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredClients = useMemo(() => {
    const query = clientQuery.trim().toLowerCase();
    if (!query) return clients;
    return clients.filter((client) =>
      [client.name, client.email, client.phone].some((value) =>
        value.toLowerCase().includes(query),
      ),
    );
  }, [clientQuery, clients]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClientSearch = (e) => {
    const query = e.target.value;
    setClientQuery(query);
    if (selectedClient) {
      setSelectedClient(null);
      setFormData({ ...formData, client: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (String(formData.area).trim() === "" || !Number.isFinite(Number(formData.area)) || Number(formData.area) < 0) {
      setSubmitError("Enter a valid non-negative project area.");
      return;
    }

    if (formData.startDate && formData.expectedEndDate && formData.expectedEndDate < formData.startDate) {
      setSubmitError("Expected end date cannot be before the project start date.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const projectDetails = { ...formData };
      delete projectDetails.client;
      const { data: responseData } = await apiClient.post("/admin/project", {
        ...projectDetails,
        area: Number(formData.area),
        startDate: formData.startDate || null,
        cost: Number(formData.cost),
        clientId: selectedClient?.id ?? null,
      });

      onCreated({
        ...(responseData?.project || responseData?.data || responseData || {}),
        ...formData,
        client: selectedClient?.name || "",
        clientId: selectedClient?.id ?? null,
      });
    } catch (error) {
      console.error("Create project request failed:", error);
      setSubmitError(
        getFriendlyErrorMessage(
          error,
          "We could not create the project. Please try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="project-modal-backdrop portal-theme" role="presentation">
      <div
        className="project-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-modal-title"
      >
        <div className="project-modal-header">
          <div>
            <p className="project-modal-eyebrow">PROJECT SETUP</p>
            <h2 className="page-title" id="project-modal-title">
              Create project
            </h2>
          </div>
          <button
            className="project-modal-close"
            type="button"
            onClick={onClose}
            aria-label="Close project form"
          >
            &times;
          </button>
        </div>

        <form className="project-card" onSubmit={handleSubmit}>
          {/* ---------- Basic Info ---------- */}
          <div className="section">
            <h3 className="section-title">Basic Info</h3>

            <div className="form-section">
              <div>
                <label htmlFor="project-title">Project name/title *</label>
                <input
                  type="text"
                  id="project-title"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleChange}
                  required
                  placeholder="Enter project name"
                />
              </div>

              <div>
                <label htmlFor="project-location">Location *</label>
                <input
                  type="text"
                  id="project-location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder="City or locality"
                />
              </div>

              <div className="project-field-wide">
                <label htmlFor="project-description">Description</label>
                <textarea
                  id="project-description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Add a short project description"
                />
              </div>
            </div>
          </div>

          {/* ---------- Project Details ---------- */}
          <div className="section">
            <h3 className="section-title">Project Details</h3>

            <div className="form-section">
              <div>
                <label htmlFor="project-address">Address *</label>
                <input
                  id="project-address"
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  placeholder="Full project address"
                />
              </div>

              <div>
                <label htmlFor="project-area">Area *</label>
                <input
                  id="project-area"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  placeholder="Example: 2500"
                />
              </div>

              <div>
                <label htmlFor="project-foundation">Foundation</label>
                <input
                  id="project-foundation"
                  type="text"
                  name="foundation"
                  value={formData.foundation}
                  onChange={handleChange}
                  placeholder="Foundation details"
                />
              </div>

              <div>
                <label htmlFor="project-category">Category *</label>
                <select
                  id="project-category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select category</option>
                  <option value="COMMERCIAL">COMMERCIAL</option>
                  <option value="RESIDENTIAL">RESIDENTIAL</option>
                  <option value="INDUSTRIAL">INDUSTRIAL</option>
                  <option value="INSTITUTIONAL">INSTITUTIONAL</option>
                  <option value="INTERIOR">INTERIOR</option>
                </select>
              </div>

              <div>
                <label htmlFor="project-cost">Project cost (₹) *</label>
                <input
                  id="project-cost"
                  type="number"
                  name="cost"
                  value={formData.cost}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                  placeholder="Enter project cost"
                />
                {formatRupees(formData.cost, "") && <small>{formatRupees(formData.cost)}</small>}
              </div>
            </div>
          </div>

          <div className="section">
            <h3 className="section-title">Client & timeline</h3>
            <label htmlFor="project-client">Select client *</label>
            <div className="project-client-search">
              <Search size={17} />
              <input
                id="project-client"
                type="search"
                value={selectedClient ? selectedClient.name : clientQuery}
                onChange={handleClientSearch}
                required={!selectedClient}
                placeholder={
                  isLoadingClients
                    ? "Loading clients..."
                    : "Search by name, email or phone"
                }
                disabled={isLoadingClients}
              />
            </div>
            {!selectedClient && clientQuery && !isLoadingClients && (
              <div className="client-search-results">
                {filteredClients.length ? (
                  filteredClients.map((client) => (
                    <button
                      type="button"
                      key={client.id}
                      onClick={() => {
                        setSelectedClient(client);
                        setClientQuery(client.name);
                        setFormData({ ...formData, client: client.name });
                      }}
                    >
                      <span className="client-result-avatar">
                        {client.name.charAt(0)}
                      </span>
                      <span className="client-result-info">
                        <strong>{client.name}</strong>
                        <span>
                          <Mail size={13} />
                          {client.email}
                        </span>
                        <span>
                          <Phone size={13} />
                          {client.phone}
                        </span>
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="client-search-empty">No clients found.</p>
                )}
              </div>
            )}
            {clientLoadError && (
              <p className="client-load-error" role="alert">
                {clientLoadError}
              </p>
            )}
            {selectedClient && (
              <div className="selected-client">
                <span className="client-result-avatar">
                  {selectedClient.name.charAt(0)}
                </span>
                <span className="selected-client-info">
                  <strong>{selectedClient.name}</strong>
                  <span>{selectedClient.email}</span>
                  <span>{selectedClient.phone}</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedClient(null);
                    setClientQuery("");
                    setFormData({ ...formData, client: "" });
                  }}
                  aria-label="Change selected client"
                >
                  <X size={15} />
                </button>
              </div>
            )}

            <label htmlFor="project-start-date">Project start date</label>
            <input
              id="project-start-date"
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
            />

            <label htmlFor="project-end-date">Expected end date</label>
            <input
              id="project-end-date"
              type="date"
              name="expectedEndDate"
              min={formData.startDate || undefined}
              value={formData.expectedEndDate}
              onChange={handleChange}
            />
          </div>

          <div className="project-form-actions">
            <button
              className="project-cancel-btn"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button className="save-btn" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create project"}
            </button>
          </div>
          {submitError && (
            <p className="client-load-error" role="alert">
              {submitError}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

export default AddNewProject;
